// plan-04 S02: vaguepost generator engine — bible S20

import { SLOT_X, SLOT_Y, SLOT_Z, SLOT_A, SLOT_PROBABLY, SLOT_QUORUM } from '../../data/slotPool'
import { VERDICTS } from '../../data/verdicts'
import { encodeHash, decodeHash, HIDDEN_MAX } from './codec'
import type { SlotTuple } from './codec'
import { load } from '../../core/save'

export interface GeneratedPost {
  tuple: SlotTuple
  xText: string
  yText: string
  zText: string
  aText: string
  hash: string
  certainty: number
  verdictText: string
  gematriaUrl: string
  probablyTag: string
  quorumTag: string
  sentence: string
}

// Mulberry32 PRNG — deterministic 32-bit
function mulberry32(seed: number): () => number {
  let state = seed | 0
  return () => {
    state = (state + 0x6D2B79F5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function pickIndex(rng: () => number, length: number): number {
  return Math.floor(rng() * length)
}

function getVerdict(certainty: number): string {
  for (const [threshold, , text] of VERDICTS) {
    if (certainty <= threshold) return text
  }
  return VERDICTS[VERDICTS.length - 1][2]
}

export interface GenerateOpts {
  fromHash?: string
  sessionN?: number
}

export function generate(opts: GenerateOpts = {}): GeneratedPost | null {
  let tuple: SlotTuple

  if (opts.fromHash) {
    const decoded = decodeHash(opts.fromHash)
    if (!decoded) return null
    tuple = decoded
  } else {
    const save = load()
    const seed = (save.firstSeenAt >>> 0) + (opts.sessionN ?? 0)
    const rng = mulberry32(seed)

    tuple = {
      x: pickIndex(rng, SLOT_X.length),
      y: pickIndex(rng, SLOT_Y.length),
      z: pickIndex(rng, SLOT_Z.length),
      a: pickIndex(rng, SLOT_A.length),
    }
  }

  const hash = encodeHash(tuple)
  const certainty = HIDDEN_MAX - (tuple.x + tuple.y + tuple.z + tuple.a)
  const verdictText = getVerdict(certainty)
  const gematriaUrl = `https://www.gematrix.org/?word=${certainty}`

  // probably/quorum tags seeded from hash for reproducibility
  const tagRng = mulberry32(hashCode(hash))
  const probablyTag = SLOT_PROBABLY[pickIndex(tagRng, SLOT_PROBABLY.length)]
  const quorumTag = SLOT_QUORUM[pickIndex(tagRng, SLOT_QUORUM.length)]

  const xText = SLOT_X[tuple.x]
  const yText = SLOT_Y[tuple.y]
  const zText = SLOT_Z[tuple.z]
  const aText = SLOT_A[tuple.a]
  const sentence = `${xText} ${yText} - ${zText} ${aText}.`

  return {
    tuple,
    xText,
    yText,
    zText,
    aText,
    hash,
    certainty,
    verdictText,
    gematriaUrl,
    probablyTag,
    quorumTag,
    sentence,
  }
}

function hashCode(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0
  }
  return h >>> 0
}
